import React from "react";

import NoDataMessage from "../../common/NoDataMessage";
import { PlatformSubscriptionRow } from "../../common/platformTypes";
import { BoolCheck } from "../BoolCheck";
import { Date as DateView } from "../Date";
import { PlatformSubscriptionLink, TeamLink } from "../DocumentLink";
import { DocumentTable, DocumentTableProperty } from "../DocumentTable";

type PlatformSubscriptionsTableProps = {
  subscriptions: PlatformSubscriptionRow[];
  hideTeamColumn?: boolean;
  /** Opt-in: only meaningful when the rows were fetched for one user. */
  showMemberRole?: boolean;
};

const asDate = (iso?: string): Date | undefined =>
  iso ? new Date(iso) : undefined;

export const PlatformSubscriptionsTable: React.FunctionComponent<
  PlatformSubscriptionsTableProps
> = ({ subscriptions, hideTeamColumn, showMemberRole }) => {
  const properties: DocumentTableProperty<PlatformSubscriptionRow>[] = [
    {
      title: "Subscription Id",
      render: (d) => (
        <PlatformSubscriptionLink docId={d._id}>
          <span className="text-monospace">{d._id}</span>
        </PlatformSubscriptionLink>
      ),
      style: { maxWidth: "24%" },
    },
    {
      title: "Team",
      render: (d) => <TeamLink docId={d.teamId}>{d.teamName}</TeamLink>,
      hide: hideTeamColumn,
    },
    {
      title: "Role",
      render: (d) =>
        d.memberRole ? (
          <span className="text-capitalize">{d.memberRole}</span>
        ) : (
          <span className="text-muted">—</span>
        ),
      hide: !showMemberRole,
    },
    { title: "Tier", render: (d) => d.tier },
    {
      // `entitled`, not `active`: a plan Stripe has not yet closed out stays
      // active:true past its end date, so the raw flag reads live when it isn't.
      title: "Active",
      render: (d) => <BoolCheck check={d.entitled} />,
    },
    { title: "Trial", render: (d) => <BoolCheck check={d.isTrial} /> },
    { title: "One Time", render: (d) => <BoolCheck check={!!d.isOneTime} /> },
    {
      title: "Seats / Licenses",
      render: (d) => `${d.seatCount} / ${d.licenses ?? "—"}`,
    },
    { title: "Start", render: (d) => <DateView date={asDate(d.startDate)} /> },
    { title: "End", render: (d) => <DateView date={asDate(d.endDate)} /> },
    {
      title: "Overrides",
      render: (d) => {
        const keys = Object.keys(d.featureOverrides ?? {});
        return keys.length ? (
          keys.join(", ")
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
  ];

  if (!subscriptions.length) {
    return <NoDataMessage componentName="subscription" />;
  }

  return (
    <DocumentTable<PlatformSubscriptionRow>
      objects={subscriptions}
      properties={properties}
    />
  );
};
